import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { ProgCss } from "./ProgCss.Generated";
import { AddPjaxUnmountListener } from "./Pjax";

function createToolbar(editor: Editor, signal: AbortSignal): HTMLElement {
    const toolbar = document.createElement("div");
    toolbar.className = ProgCss.tiptap_toolbar;

    const buttons: { label: string; icon: string; action: () => void; isActive?: () => boolean; isEnabled?: () => boolean }[] = [
        { label: "Bold", icon: `<svg width="24" height="24" viewBox="0 0 24 24"><path d="M7.8 19c-.3 0-.5 0-.6-.2l-.2-.5V5.7c0-.2 0-.4.2-.5l.6-.2h5c1.5 0 2.7.3 3.5 1 .7.6 1.1 1.4 1.1 2.5a3 3 0 0 1-.6 1.9c-.4.6-1 1-1.6 1.2.4.1.9.3 1.3.6s.8.7 1 1.2c.4.4.5 1 .5 1.6 0 1.3-.4 2.3-1.3 3-.8.7-2.1 1-3.8 1H7.8Zm5-8.3c.6 0 1.2-.1 1.6-.5.4-.3.6-.7.6-1.3 0-1.1-.8-1.7-2.3-1.7H9.3v3.5h3.4Zm.5 6c.7 0 1.3-.1 1.7-.4.4-.4.6-.9.6-1.5s-.2-1-.7-1.4c-.4-.3-1-.4-2-.4H9.4v3.8h4Z" fill-rule="evenodd"/></svg>`, action: () => { editor.chain().focus().toggleBold().run(); }, isActive: () => editor.isActive("bold") },
        { label: "Italic", icon: `<svg width="24" height="24" viewBox="0 0 24 24"><path d="m16.7 4.7-.1.9h-.3c-.6 0-1 0-1.4.3-.3.3-.4.6-.5 1.1l-2.1 9.8v.6c0 .5.4.8 1.4.8h.2l-.2.8H8l.2-.8h.2c1.1 0 1.8-.5 2-1.5l2-9.8.1-.5c0-.6-.4-.8-1.4-.8h-.3l.2-.9h5.8Z" fill-rule="evenodd"/></svg>`, action: () => { editor.chain().focus().toggleItalic().run(); }, isActive: () => editor.isActive("italic") },
        { label: "Underline", icon: `<svg width="24" height="24" viewBox="0 0 24 24"><path d="M16 5c.6 0 1 .4 1 1v5.5a4 4 0 0 1-.4 1.8l-1 1.4a5.3 5.3 0 0 1-5.5 1 5 5 0 0 1-1.6-1c-.5-.4-.8-.9-1.1-1.4a4 4 0 0 1-.4-1.8V6c0-.6.4-1 1-1s1 .4 1 1v5.5c0 .3 0 .6.2 1l.6.7a3.3 3.3 0 0 0 2.2.8 3.4 3.4 0 0 0 2.2-.8c.3-.2.4-.5.6-.8l.2-.9V6c0-.6.4-1 1-1ZM8 17h8c.6 0 1 .4 1 1s-.4 1-1 1H8a1 1 0 0 1 0-2Z" fill-rule="evenodd"/></svg>`, action: () => { editor.chain().focus().toggleUnderline().run(); }, isActive: () => editor.isActive("underline") },
        { label: "Superscript", icon: `<svg width="24" height="24" viewBox="0 0 24 24"><path d="M15 9.4 10.4 14l4.6 4.6-1.4 1.4L9 15.4 4.4 20 3 18.6 7.6 14 3 9.4 4.4 8 9 12.6 13.6 8 15 9.4Zm5.9 1.6h-5v-1l1-.8 1.7-1.6c.3-.5.5-.9.5-1.3 0-.3 0-.5-.2-.7-.2-.2-.5-.3-.9-.3l-.8.2-.7.4-.4-1.2c.2-.2.5-.4 1-.5.3-.2.8-.2 1.2-.2.8 0 1.4.2 1.8.6.4.4.6 1 .6 1.6 0 .5-.2 1-.5 1.5l-1.3 1.4-.6.5h2.6V11Z" fill-rule="nonzero"/></svg>`, action: () => { editor.chain().focus().toggleSuperscript().run(); }, isActive: () => editor.isActive("superscript") },
        { label: "Subscript", icon: `<svg width="24" height="24" viewBox="0 0 24 24"><path d="m10.4 10 4.6 4.6-1.4 1.4L9 11.4 4.4 16 3 14.6 7.6 10 3 5.4 4.4 4 9 8.6 13.6 4 15 5.4 10.4 10ZM21 19h-5v-1l1-.8 1.7-1.6c.3-.4.5-.8.5-1.2 0-.3 0-.6-.2-.7-.2-.2-.5-.3-.9-.3a2 2 0 0 0-.8.2l-.7.3-.4-1.1 1-.6 1.2-.2c.8 0 1.4.3 1.8.7.4.4.6.9.6 1.5s-.2 1.1-.5 1.6a8 8 0 0 1-1.3 1.3l-.6.6h2.6V19Z" fill-rule="nonzero"/></svg>`, action: () => { editor.chain().focus().toggleSubscript().run(); }, isActive: () => editor.isActive("subscript") },
        { label: "Clear formatting", icon: `<svg width="24" height="24" viewBox="0 0 24 24"><path d="M13.2 6a1 1 0 0 1 0 .2l-2.6 10a1 1 0 0 1-1 .8h-.2a.8.8 0 0 1-.8-1l2.6-10H8a1 1 0 1 1 0-2h9a1 1 0 0 1 0 2h-3.8ZM5 18h7a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Zm13 1.5L16.5 18 15 19.5a.7.7 0 0 1-1-1l1.5-1.5-1.5-1.5a.7.7 0 0 1 1-1l1.5 1.5 1.5-1.5a.7.7 0 0 1 1 1L17.5 17l1.5 1.5a.7.7 0 0 1-1 1Z" fill-rule="evenodd"/></svg>`, action: () => { editor.chain().focus().unsetAllMarks().run(); if (editor.state.selection.empty) { editor.view.dispatch(editor.state.tr.setStoredMarks([])); } } },
        { label: "Ordered list", icon: `<svg width="24px" height="24px" viewBox="0 -960 960 960" fill="#e3e3e3"><path d="M120-80v-60h100v-30h-60v-60h60v-30H120v-60h120q17 0 28.5 11.5T280-280v40q0 17-11.5 28.5T240-200q17 0 28.5 11.5T280-160v40q0 17-11.5 28.5T240-80H120Zm0-280v-110q0-17 11.5-28.5T160-510h60v-30H120v-60h120q17 0 28.5 11.5T280-560v70q0 17-11.5 28.5T240-450h-60v30h100v60H120Zm60-280v-180h-60v-60h120v240h-60Zm180 440v-80h480v80H360Zm0-240v-80h480v80H360Zm0-240v-80h480v80H360Z"/></svg>`, action: () => { editor.chain().focus().toggleOrderedList().run(); }, isActive: () => editor.isActive("orderedList") },
        { label: "Bullet list", icon: `<svg width="24px" height="24px" viewBox="0 -960 960 960" fill="#e3e3e3"><path d="M360-200v-80h480v80H360Zm0-240v-80h480v80H360Zm0-240v-80h480v80H360ZM200-160q-33 0-56.5-23.5T120-240q0-33 23.5-56.5T200-320q33 0 56.5 23.5T280-240q0 33-23.5 56.5T200-160Zm0-240q-33 0-56.5-23.5T120-480q0-33 23.5-56.5T200-560q33 0 56.5 23.5T280-480q0 33-23.5 56.5T200-400Zm-56.5-263.5Q120-687 120-720t23.5-56.5Q167-800 200-800t56.5 23.5Q280-753 280-720t-23.5 56.5Q233-640 200-640t-56.5-23.5Z"/></svg>`, action: () => { editor.chain().focus().toggleBulletList().run(); }, isActive: () => editor.isActive("bulletList") },
        { label: "Blockquote", icon: `<svg width="24" height="24" viewBox="0 0 24 24"><path d="M7.5 17h.9c.4 0 .7-.2.9-.6L11 13V8c0-.6-.4-1-1-1H6a1 1 0 0 0-1 1v4c0 .6.4 1 1 1h2l-1.3 2.7a1 1 0 0 0 .8 1.3Zm8 0h.9c.4 0 .7-.2.9-.6L19 13V8c0-.6-.4-1-1-1h-4a1 1 0 0 0-1 1v4c0 .6.4 1 1 1h2l-1.3 2.7a1 1 0 0 0 .8 1.3Z" fill-rule="nonzero"/></svg>`, action: () => { editor.chain().focus().toggleBlockquote().run(); }, isActive: () => editor.isActive("blockquote") },
        { label: "Link", icon: `<svg width="24" height="24" viewBox="0 0 24 24"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1ZM8 13h8v-2H8v2Zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5Z" fill-rule="nonzero"/></svg>`, action: () => { const isLink = editor.isActive("link"); const currentUrl = isLink ? (editor.getAttributes("link").href as string | undefined) ?? "https://" : "https://"; const url = window.prompt("URL", currentUrl); if (url === null) { return; } if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; } editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run(); }, isActive: () => editor.isActive("link") },
        { label: "Remove link", icon: `<svg width="24" height="24" viewBox="0 0 24 24"><path d="M17 7h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1 0 1.43-.98 2.63-2.31 2.98l1.46 1.46C20.88 15.61 22 13.95 22 12c0-2.76-2.24-5-5-5Zm-1 4h-2.19l2 2H16v-2ZM2 4.27l3.11 3.11C3.29 8.12 2 9.91 2 12c0 2.76 2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1 0-1.59 1.21-2.9 2.76-3.07L8.73 11H8v2h2.73L13 15.27V17h1.73l4.01 4.01 1.41-1.41L3.41 2.86 2 4.27Z" fill-rule="nonzero"/></svg>`, action: () => { editor.chain().focus().extendMarkRange("link").unsetLink().run(); }, isEnabled: () => editor.isActive("link") },
        { label: "Table", icon: `<svg width="24" height="24" viewBox="0 0 24 24"><path fill-rule="nonzero" d="M19 4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6c0-1.1.9-2 2-2h14ZM5 14v4h6v-4H5Zm14 0h-6v4h6v-4Zm0-6h-6v4h6V8ZM5 12h6V8H5v4Z"/></svg>`, action: () => {} },
        { label: "Undo", icon: `<svg width="24" height="24" viewBox="0 0 24 24"><path d="M6.4 8H12c3.7 0 6.2 2 6.8 5.1.6 2.7-.4 5.6-2.3 6.8a1 1 0 0 1-1-1.8c1.1-.6 1.8-2.7 1.4-4.6-.5-2.1-2.1-3.5-4.9-3.5H6.4l3.3 3.3a1 1 0 1 1-1.4 1.4l-5-5a1 1 0 0 1 0-1.4l5-5a1 1 0 0 1 1.4 1.4L6.4 8Z" fill-rule="nonzero"/></svg>`, action: () => { editor.chain().focus().undo().run(); } },
        { label: "Redo", icon: `<svg width="24" height="24" viewBox="0 0 24 24"><path d="M17.6 10H12c-2.8 0-4.4 1.4-4.9 3.5-.4 2 .3 4 1.4 4.6a1 1 0 1 1-1 1.8c-2-1.2-2.9-4.1-2.3-6.8.6-3 3-5.1 6.8-5.1h5.6l-3.3-3.3a1 1 0 1 1 1.4-1.4l5 5a1 1 0 0 1 0 1.4l-5 5a1 1 0 0 1-1.4-1.4l3.3-3.3Z" fill-rule="nonzero"/></svg>`, action: () => { editor.chain().focus().redo().run(); } },
    ];

    const tableButtonIndex = buttons.findIndex(b => b.label === "Table");

    for (let i = 0; i < tableButtonIndex; i++) {
        toolbar.appendChild(createToolbarButton(buttons[i], updateActiveStates));
    }

    const tableMenu = createTableMenu(editor, signal);
    toolbar.appendChild(tableMenu);

    const dropdown = createStyleDropdown(editor, signal);
    toolbar.appendChild(dropdown);

    for (let i = tableButtonIndex + 1; i < buttons.length; i++) {
        toolbar.appendChild(createToolbarButton(buttons[i], updateActiveStates));
    }

    const spacer = document.createElement("div");
    spacer.className = ProgCss.tiptap_toolbar_spacer;
    toolbar.appendChild(spacer);

    const sourceButton = document.createElement("button");
    sourceButton.type = "button";
    sourceButton.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24"><path d="M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4Zm5.2 0L19.2 12l-4.6-4.6L16 6l6 6-6 6-1.4-1.4Z" fill-rule="evenodd"/></svg>`;
    sourceButton.title = "Source";
    sourceButton.className = `${ProgCss.tiptap_toolbar_button} ${ProgCss.tiptap_toolbar_button_source}`;
    toolbar.appendChild(sourceButton);

function updateActiveStates() {
        const toolbarButtons = toolbar.querySelectorAll(`.${ProgCss.tiptap_toolbar_button}`);
        toolbarButtons.forEach((el) => {
            const label = el.getAttribute("title") ?? "";
            const buttonDef = buttons.find(b => b.label === label);
            if (buttonDef?.isActive) {
                const isActive = buttonDef.isActive();
                el.classList.toggle(ProgCss.tiptap_active, isActive);
                el.setAttribute("aria-pressed", isActive ? "true" : "false");
            }
            if (buttonDef?.isEnabled) {
                (el as HTMLButtonElement).disabled = !buttonDef.isEnabled();
            }
        });
        updateDropdownLabel(editor, dropdown);
    }

    editor.on("selectionUpdate", updateActiveStates);
    editor.on("transaction", updateActiveStates);

    return toolbar;
}

function createToolbarButton(buttonDef: { label: string; icon: string; action: () => void; isActive?: () => boolean; isEnabled?: () => boolean }, updateActiveStates: () => void): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.innerHTML = buttonDef.icon;
    button.title = buttonDef.label;
    button.setAttribute("aria-label", buttonDef.label);
    if (buttonDef.isActive) {
        button.setAttribute("aria-pressed", "false");
    }
    if (buttonDef.isEnabled) {
        button.disabled = !buttonDef.isEnabled();
    }
    button.className = ProgCss.tiptap_toolbar_button;
    button.addEventListener("click", (e) => {
        e.preventDefault();
        buttonDef.action();
        updateActiveStates();
    });
    return button;
}

type StyleOption = { label: string; level?: number };

const styleOptions: StyleOption[] = [
    { label: "Paragraph" },
    { label: "Heading 1", level: 1 },
    { label: "Heading 2", level: 2 },
    { label: "Heading 3", level: 3 },
    { label: "Heading 4", level: 4 },
    { label: "Heading 5", level: 5 },
    { label: "Heading 6", level: 6 },
    { label: "Preformatted" },
];

function getCurrentStyleLabel(editor: Editor): string {
    for (let level = 1; level <= 6; level++) {
        if (editor.isActive("heading", { level })) {
            return `Heading ${level}`;
        }
    }
    if (editor.isActive("codeBlock")) {
        return "Preformatted";
    }
    return "Paragraph";
}

function updateDropdownLabel(editor: Editor, dropdown: HTMLElement) {
    const label = dropdown.querySelector(`.${ProgCss.tiptap_style_dropdown_label}`) as HTMLElement;
    if (label) {
        label.textContent = getCurrentStyleLabel(editor);
    }
}

function createStyleDropdown(editor: Editor, signal: AbortSignal): HTMLElement {
    const container = document.createElement("div");
    container.className = ProgCss.tiptap_style_dropdown;

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = ProgCss.tiptap_style_dropdown_toggle;
    toggle.innerHTML = `<span class="${ProgCss.tiptap_style_dropdown_label}">${getCurrentStyleLabel(editor)}</span><svg width="12" height="12" viewBox="0 0 12 12"><path d="M3 5l3 3 3-3" fill="none" stroke="#999" stroke-width="1.5"/></svg>`;

    const menu = document.createElement("div");
    menu.className = ProgCss.tiptap_style_dropdown_menu;

    for (const option of styleOptions) {
        const item = document.createElement("button");
        item.type = "button";
        item.className = ProgCss.tiptap_style_dropdown_item;
        const tag = option.level ? `h${option.level}` : option.label === "Preformatted" ? "pre" : "p";
        const tagEl = document.createElement(tag);
        tagEl.textContent = option.label;
        item.appendChild(tagEl);
        item.addEventListener("click", (e) => {
            e.preventDefault();
            if (option.level) {
                editor.chain().focus().toggleHeading({ level: option.level as 1 | 2 | 3 | 4 | 5 | 6 }).run();
            } else if (option.label === "Preformatted") {
                editor.chain().focus().toggleCodeBlock().run();
            } else {
                editor.chain().focus().setParagraph().run();
            }
            menu.classList.remove(ProgCss.tiptap_open);
        });
        menu.appendChild(item);
    }

    toggle.addEventListener("click", (e) => {
        e.preventDefault();
        const isOpen = menu.classList.toggle(ProgCss.tiptap_open);
        if (isOpen) {
            const currentLabel = getCurrentStyleLabel(editor);
            menu.querySelectorAll(`.${ProgCss.tiptap_style_dropdown_item}`).forEach(el => {
                el.classList.toggle(ProgCss.tiptap_active, el.textContent === currentLabel);
            });
        }
    });

    document.addEventListener("click", (e) => {
        if (!container.contains(e.target as Node)) {
            menu.classList.remove(ProgCss.tiptap_open);
        }
    }, { signal });

    container.appendChild(toggle);
    container.appendChild(menu);
    return container;
}

function createTableMenu(editor: Editor, signal: AbortSignal): HTMLElement {
    const container = document.createElement("div");
    container.className = ProgCss.tiptap_table_menu;

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = `${ProgCss.tiptap_toolbar_button} ${ProgCss.tiptap_table_menu_toggle}`;
    toggle.title = "Table";
    toggle.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24"><path fill-rule="nonzero" d="M19 4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6c0-1.1.9-2 2-2h14ZM5 14v4h6v-4H5Zm14 0h-6v4h6v-4Zm0-6h-6v4h6V8ZM5 12h6V8H5v4Z"/></svg><svg class="${ProgCss.tiptap_toolbar_chevron}" width="6" height="6" viewBox="0 0 12 12"><path d="M3 5l3 3 3-3" fill="none" stroke="#999" stroke-width="1.5"/></svg>`;

    const menu = document.createElement("div");
    menu.className = ProgCss.tiptap_table_menu_panel;

    const submenus: { title: string; content: (parent: HTMLElement) => void }[] = [
        {
            title: "Table",
            content: (parent) => {
                const gridLabel = document.createElement("div");
                gridLabel.className = ProgCss.tiptap_table_menu_label;
                gridLabel.textContent = "Insert table";
                parent.appendChild(gridLabel);

                const gridContainer = document.createElement("div");
                gridContainer.className = ProgCss.tiptap_table_grid;
                const maxRows = 8;
                const maxCols = 8;
                let hoveredRow = 0;
                let hoveredCol = 0;

                const gridSizeLabel = document.createElement("div");
                gridSizeLabel.className = ProgCss.tiptap_table_grid_size;
                gridSizeLabel.textContent = "";

                for (let r = 1; r <= maxRows; r++) {
                    for (let c = 1; c <= maxCols; c++) {
                        const cell = document.createElement("div");
                        cell.className = ProgCss.tiptap_table_grid_cell;
                        cell.dataset.row = String(r);
                        cell.dataset.col = String(c);
                        cell.tabIndex = 0;
                        cell.setAttribute("role", "button");
                        cell.setAttribute("aria-label", `Insert table ${r} by ${c}`);
                        cell.addEventListener("mouseenter", () => {
                            hoveredRow = r;
                            hoveredCol = c;
                            gridSizeLabel.textContent = `${r} \u00d7 ${c}`;
                            updateGridHighlight();
                        });
                        const insertTable = () => {
                            editor.chain().focus().insertTable({ rows: r, cols: c, withHeaderRow: true }).run();
                            menu.classList.remove(ProgCss.tiptap_open);
                        };
                        cell.addEventListener("click", (e) => {
                            e.preventDefault();
                            insertTable();
                        });
                        cell.addEventListener("keydown", (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                insertTable();
                            }
                        });
                        gridContainer.appendChild(cell);
                    }
                }

                function updateGridHighlight() {
                    gridContainer.querySelectorAll(`.${ProgCss.tiptap_table_grid_cell}`).forEach(cell => {
                        const cr = Number((cell as HTMLElement).dataset.row);
                        const cc = Number((cell as HTMLElement).dataset.col);
                        cell.classList.toggle(ProgCss.is_highlighted, cr <= hoveredRow && cc <= hoveredCol);
                    });
                }

                parent.appendChild(gridContainer);
                parent.appendChild(gridSizeLabel);

                const separator = document.createElement("div");
                separator.className = ProgCss.tiptap_table_menu_separator;
                parent.appendChild(separator);

                const headerBtn = document.createElement("button");
                headerBtn.type = "button";
                headerBtn.className = ProgCss.tiptap_table_menu_item;
                headerBtn.textContent = "Toggle header row";
                headerBtn.addEventListener("click", (e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleHeaderRow().run();
                    menu.classList.remove(ProgCss.tiptap_open);
                });
                parent.appendChild(headerBtn);
            },
        },
        {
            title: "Row",
            content: (parent) => {
                const items = [
                    { label: "Add row above", action: () => { editor.chain().focus().addRowBefore().run(); } },
                    { label: "Add row below", action: () => { editor.chain().focus().addRowAfter().run(); } },
                    { label: "Delete row", action: () => { editor.chain().focus().deleteRow().run(); } },
                ];
                for (const act of items) {
                    const item = document.createElement("button");
                    item.type = "button";
                    item.className = ProgCss.tiptap_table_menu_item;
                    item.textContent = act.label;
                    item.addEventListener("click", (e) => {
                        e.preventDefault();
                        act.action();
                        menu.classList.remove(ProgCss.tiptap_open);
                    });
                    parent.appendChild(item);
                }
            },
        },
        {
            title: "Column",
            content: (parent) => {
                const items = [
                    { label: "Add column before", action: () => { editor.chain().focus().addColumnBefore().run(); } },
                    { label: "Add column after", action: () => { editor.chain().focus().addColumnAfter().run(); } },
                    { label: "Delete column", action: () => { editor.chain().focus().deleteColumn().run(); } },
                ];
                for (const act of items) {
                    const item = document.createElement("button");
                    item.type = "button";
                    item.className = ProgCss.tiptap_table_menu_item;
                    item.textContent = act.label;
                    item.addEventListener("click", (e) => {
                        e.preventDefault();
                        act.action();
                        menu.classList.remove(ProgCss.tiptap_open);
                    });
                    parent.appendChild(item);
                }
            },
        },
    ];

    for (const sub of submenus) {
        const menuItem = document.createElement("div");
        menuItem.className = ProgCss.tiptap_table_submenu;

        const trigger = document.createElement("button");
        trigger.type = "button";
        trigger.className = `${ProgCss.tiptap_table_menu_item} ${ProgCss.tiptap_table_submenu_trigger}`;
        trigger.innerHTML = `${sub.title}<svg width="12" height="12" viewBox="0 0 12 12"><path d="M5 3l3 3-3 3" fill="none" stroke="#999" stroke-width="1.5"/></svg>`;

        const subPanel = document.createElement("div");
        subPanel.className = ProgCss.tiptap_table_submenu_panel;
        sub.content(subPanel);

        menuItem.appendChild(trigger);
        menuItem.appendChild(subPanel);
        menu.appendChild(menuItem);
    }

    const deleteTableSeparator = document.createElement("div");
    deleteTableSeparator.className = ProgCss.tiptap_table_menu_separator;
    menu.appendChild(deleteTableSeparator);

    const deleteTableBtn = document.createElement("button");
    deleteTableBtn.type = "button";
    deleteTableBtn.className = ProgCss.tiptap_table_menu_item;
    deleteTableBtn.textContent = "Delete table";
    deleteTableBtn.addEventListener("click", (e) => {
        e.preventDefault();
        editor.chain().focus().deleteTable().run();
        menu.classList.remove(ProgCss.tiptap_open);
    });
    menu.appendChild(deleteTableBtn);

    toggle.addEventListener("click", (e) => {
        e.preventDefault();
        menu.classList.toggle(ProgCss.tiptap_open);
    });

    document.addEventListener("click", (e) => {
        if (!container.contains(e.target as Node)) {
            menu.classList.remove(ProgCss.tiptap_open);
        }
    }, { signal });

    container.appendChild(toggle);
    container.appendChild(menu);
    return container;
}

export function render(element: HTMLElement) {
    if (element.nextSibling instanceof HTMLElement && element.nextSibling.classList.contains(ProgCss.tiptap_editor_wrapper)) {
        return;
    }
    const parentNode = element.parentNode;
    if (!parentNode) {
        return;
    }

    const abortController = new AbortController();
    const wrapper = document.createElement("div");
    wrapper.className = ProgCss.tiptap_editor_wrapper;
    element.style.display = "none";
    parentNode.insertBefore(wrapper, element.nextSibling);

    const editorContainer = document.createElement("div");
    editorContainer.className = ProgCss.tiptap_editor_content;

    const editor = new Editor({
        element: editorContainer,
        extensions: [
            StarterKit.configure({ link: { openOnClick: false, HTMLAttributes: { target: null, rel: null } } }),
            Superscript,
            Subscript,
            Table.configure({ resizable: false }),
            TableRow,
            TableCell,
            TableHeader,
        ],
        content: (element as HTMLTextAreaElement).value ?? (element as HTMLElement).innerHTML,
        onUpdate: ({ editor: ed }) => {
            if (element instanceof HTMLTextAreaElement || element instanceof HTMLInputElement) {
                element.value = ed.getHTML();
            } else {
                element.innerHTML = ed.getHTML();
            }
        },
    });

    const sourceArea = document.createElement("textarea");
    sourceArea.className = ProgCss.tiptap_source_editor;
    sourceArea.style.display = "none";

    const toolbar = createToolbar(editor, abortController.signal);
    const sourceButton = toolbar.querySelector(`.${ProgCss.tiptap_toolbar_button}[title='Source']`) as HTMLButtonElement;
    sourceButton.setAttribute("aria-pressed", "false");
    let isSourceMode = false;

    sourceButton.addEventListener("click", (e) => {
        e.preventDefault();
        isSourceMode = !isSourceMode;
        sourceButton.classList.toggle(ProgCss.tiptap_active, isSourceMode);
        sourceButton.setAttribute("aria-pressed", isSourceMode ? "true" : "false");
        if (isSourceMode) {
            sourceArea.style.height = editorContainer.offsetHeight + "px";
            sourceArea.value = editor.getHTML();
            editorContainer.style.display = "none";
            sourceArea.style.display = "block";
        } else {
            editor.commands.setContent(sourceArea.value);
            sourceArea.style.display = "none";
            editorContainer.style.display = "block";
        }
    });

    sourceArea.addEventListener("input", () => {
        if (element instanceof HTMLTextAreaElement || element instanceof HTMLInputElement) {
            element.value = sourceArea.value;
        } else {
            element.innerHTML = sourceArea.value;
        }
    });

    wrapper.appendChild(toolbar);
    wrapper.appendChild(editorContainer);
    wrapper.appendChild(sourceArea);

    AddPjaxUnmountListener(() => {
        editor.destroy();
        abortController.abort();
    });
}
